import { type FC } from 'react';
import { Controller, type SubmitHandler } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast, Button, DropSelect, FormSection, Input, Separator } from '@zawarski/eti-ui';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { userSelector } from '@/store/auth/auth.selectors';
import { useContactSupportMutation } from '@/services/notificationsApi';
import { formatCatchErrorMsg } from '@/utils';
import useContactSupportForm, {
  defaultValues,
  type ContactSupportFormValues,
} from '@/hooks/useContactSupportForm';

/**
 * Contact Support Flow:
 *
 *  DrawerMenu "Contact Support" click
 *       │
 *       ▼
 *  handleContactSupport() → CustomEvent('open-contact-support')
 *       │
 *       ▼
 *  MainLayout useEffect listener → setContactSupportOpen(true)
 *       │
 *       ▼
 *  <ContactSupportModal open={true} />
 *       │
 *       ├── Auto-populate: name, email, facility from userSelector
 *       ▼
 *  User fills: Subject (dropdown) + Details (textarea)
 *       │
 *       ▼
 *  Submit → contactSupport mutation (POST /api/notifications/contact-support)
 *       ├── success → toast success + close modal + reset form
 *       └── failure → toast error
 */

interface Props {
  open: boolean;
  onClose: () => void;
}

const subjectOptions = [
  { label: 'Report a bug', value: 'Report a bug' },
  { label: 'Suggestion/Improvements', value: 'Suggestion/Improvements' },
  { label: 'Technical support', value: 'Technical support' },
];

const ContactSupportModal: FC<Props> = ({ open, onClose }) => {
  const user = useSelector(userSelector);
  const form = useContactSupportForm();
  const [submitContactSupport, { isLoading: submitting }] = useContactSupportMutation();

  const fullName = [user?.pvPersonGivenName, user?.pvPersonSurName].filter(Boolean).join(' ');
  const email = user?.email ?? '';
  const facility = user?.pvOrganizationUnitName ?? '';

  const onSubmit: SubmitHandler<ContactSupportFormValues> = async (data) => {
    try {
      await submitContactSupport({
        subject: data.subject,
        details: data.details,
        fullName,
        email,
        facility,
      }).unwrap();
      toast.success('Your support request has been submitted.');
      form.reset(defaultValues);
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(formatCatchErrorMsg(error));
    }
  };

  const handleClose = () => {
    if (submitting) return;
    form.reset(defaultValues);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent
        className={'hc:border hc:border-border hc:p-0 hc:md:min-w-3xl'}
        showCloseButton={false}>
        <DialogHeader className={'hc:px-6 hc:py-4'}>
          <DialogTitle
            className={
              'hc:text-typography-dark hc:dark:text-foreground hc:text-lg hc:font-semibold'
            }>
            Feel free to contact us
          </DialogTitle>
          <p className={'hc:text-sm hc:text-muted-foreground'}>
            A response will be provided within 2 working days.
          </p>
        </DialogHeader>
        <Separator orientation={'horizontal'} />

        <div className={'hc:flex hc:flex-col hc:w-full hc:px-6 hc:py-4 hc:gap-4'}>
          <FormSection uppercase title={'Contact Support'} textSize={'md'}>
            <div className={'hc:flex hc:flex-col hc:w-full hc:gap-3 hc:p-4'}>
              {/* Row 1: Subject + Facility/Group */}
              <div className={'hc:grid hc:grid-cols-1 hc:md:grid-cols-2 hc:gap-4'}>
                <Controller
                  control={form.control}
                  name={'subject'}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <div className={'hc:flex hc:flex-col hc:gap-1'}>
                      <DropSelect
                        fullWidth
                        required
                        label={'Subject'}
                        options={subjectOptions}
                        value={value}
                        onChange={onChange}
                        placeholder={'Select Subject'}
                      />
                      {error && <p className={'hc:text-xs hc:text-destructive'}>{error.message}</p>}
                    </div>
                  )}
                />
                <Input label={'Facility / Group'} value={facility} readOnly disabled />
              </div>

              {/* Row 2: Full Name + Email (read-only) */}
              <div className={'hc:grid hc:grid-cols-1 hc:md:grid-cols-2 hc:gap-4'}>
                <Input label={'Full name'} value={fullName} readOnly disabled />
                <Input label={'Email address'} value={email} readOnly disabled />
              </div>

              {/* Row 3: Details */}
              <Controller
                control={form.control}
                name={'details'}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    multiple
                    required
                    rows={4}
                    label={'Details'}
                    placeholder={'Describe your issue or request'}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </div>
          </FormSection>
        </div>

        <Separator orientation={'horizontal'} />
        <DialogFooter className='hc:flex-row hc:gap-2 hc:justify-end hc:pr-4 hc:pb-4'>
          <Button
            variant={'secondary'}
            disabled={submitting}
            onClick={handleClose}
            children={'Cancel'}
          />
          <Button
            loading={submitting}
            onClick={!submitting ? form.handleSubmit(onSubmit) : undefined}
            children={'Submit'}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContactSupportModal;
